import { useFeatureFlagClient } from '@/composables/featureFlags.js'
import { useAuth } from '@/composables/useAuth.js'
import { ApiError } from '@/composables/useApi.js'
import { isSamePage } from '@/utils/middleware.js'

export default defineNuxtRouteMiddleware(async (to, from) => {
  if (isSamePage(to, from) || to.meta?.skipMiddleware) {
    return
  }

  const {
    fetchCurrentUser,
    fetchAvailableDepartments,
    fetchNavigationSidebar,
    fetchMergedSettings,
    fetchCurrentOrganization,
    fetchActivePermissions,
    forceLogin,
    isLoggedIn,
  } = useAuth()
  const config = useRuntimeConfig()

  // skip on '/init' when in test mode (used in fixtures.ts)
  if (to.path === '/init' && config.public.isTest) {
    return
  }

  // skip on login and verify pages - they handle their own auth
  if (
    to.name === 'provetId-auth-login' ||
    to.name === 'provetId-auth-login-verify'
  ) {
    return
  }

  // we need a provetId to continue
  if (!to.params.provetId) {
    return
  }

  // no need to re-fetch
  if (isLoggedIn.value) {
    return
  }

  const bootstrapResults = await Promise.allSettled([
    fetchCurrentUser(to),
    fetchAvailableDepartments(to),
    fetchNavigationSidebar(to),
    fetchMergedSettings(to),
    fetchCurrentOrganization(to),
    fetchActivePermissions(to),
  ])

  const rejected = bootstrapResults
    .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
    .map((r) => r.reason)

  const nonFeLogin403Error = rejected.find(
    (error) =>
      !(config.public.isFELogin && error instanceof ApiError && error.is(403)),
  )

  if (nonFeLogin403Error) {
    throw nonFeLogin403Error
  }

  // If FE login is enabled and user is still not logged in after fetch, redirect to login
  if (config.public.isFELogin && !isLoggedIn.value) {
    return forceLogin(to)
  }

  // Explicitly re-identify with LaunchDarkly now that we have auth context (including activeDepartment)
  // This ensures flags and guards see the correct context before the page renders.
  try {
    const featureFlagClient = useFeatureFlagClient(to)
    await featureFlagClient?.updateLaunchDarklyContext()
  } catch {
    // Don't block navigation if update fails; page should still render with original context.
  }
})
